import { ArrowRight } from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { useAuth } from '@/lib/config/auth-provider'
import { cn } from '@/lib/utils'
import { FadeUp } from '../../FadeUp'

interface WelcomeSectionProps {
  orgName: string
  /** Whether the user was invited into an existing org (vs. creating a new one). */
  isInvitedUser: boolean
  onNext: () => void
  className?: string
}

/**
 * First section of the Getting Started Guide.
 * Greets the user by first name, confirms account setup, and offers
 * a single CTA — "Show me how it works" — that advances to the next section.
 * There is intentionally no skip or back action here.
 */
export function WelcomeSection({ orgName, isInvitedUser, onNext, className }: WelcomeSectionProps) {
  const { t } = useTranslation('onboarding')
  const { user } = useAuth()
  // Extract first name from full name, fall back gracefully to empty string
  const firstName = user?.name?.split(' ')[0] ?? ''

  return (
    <div
      className={cn('flex flex-col items-center gap-8 py-4 text-center', className)}
      data-testid="guide-welcome-section"
    >
      {/* Heading — delay 0 */}
      <FadeUp delay={0} className="flex flex-col gap-3">
        <Heading level={1} size="4xl">
          {firstName ? t('guide.letsGetStartedNamed', { name: firstName }) : t('guide.letsGetStarted')}
        </Heading>

        {!isInvitedUser && orgName && (
          <Text size="base" color="muted">
            <Trans
              t={t}
              i18nKey="guide.orgCreated"
              values={{ org: orgName }}
              components={{ org: <span className="font-semibold text-foreground" /> }}
            />
          </Text>
        )}
      </FadeUp>

      {/* CTA — delay 120ms */}
      <FadeUp delay={120}>
        <Button
          data-testid="guide-welcome-show-how-button"
          variant="cta"
          size="lg"
          onClick={onNext}
          className="gap-2 px-8"
        >
          {t('guide.showHow')}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </FadeUp>
    </div>
  )
}
